import { Button } from "@fluentui/react-components";
import { FilteringCombobox } from "components/controls/FilteringCombobox";
import { Flex } from "components/styled";
import { useCurrencyRates } from "hooks/useCurrencyRates";
import { useGetCurrencyOptionsForRateCalculation } from "hooks/useGetCurrencyOptionsForRateCalculation";
import { useAtomValue } from "jotai";
import { allCurrenciesOption } from "library/constants";
import { ICompanyInfosInfo, SelectItem } from "library/types";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { filterCurrencyAtom } from "store/UIHrPageDashboard";

type Props = {
  defaultCompanyInfo: ICompanyInfosInfo;
  setCompanyInfo: Dispatch<SetStateAction<ICompanyInfosInfo | undefined>>;
  convertCurrencyDisable: boolean;
  setConvertCurrencyDisabled: Dispatch<SetStateAction<boolean>>;
  selectedCurrency: SelectItem | null;
  setSelectedCurrency: Dispatch<SetStateAction<SelectItem | null>>;
  defaultCurrencyByUserCountry?: SelectItem | null;
};

function SummaryCurrencyConverter({
  defaultCompanyInfo,
  setCompanyInfo,
  convertCurrencyDisable,
  setConvertCurrencyDisabled,
  selectedCurrency,
  setSelectedCurrency,
  defaultCurrencyByUserCountry,
}: Props) {
  const { t } = useTranslation();
  const filterCurrency = useAtomValue(filterCurrencyAtom);

  const currencyOptions = useGetCurrencyOptionsForRateCalculation();

  const { currencyRatesLoading, currencyRatesPerCountries } =
    useCurrencyRates();

  const convertEnrollmentRates = useCallback(
    (companyInfo: ICompanyInfosInfo) => {
      const calculateInEur = selectedCurrency?.codeShort === "EUR";
      const selectedRate = selectedCurrency?.value
        ? currencyRatesPerCountries[selectedCurrency.value]?.rate
        : undefined;

      if (!calculateInEur && !selectedRate) {
        return companyInfo?.employeeEnrollments ?? [];
      }

      return (
        companyInfo?.employeeEnrollments?.map((enrollment) => {
          const implementation = companyInfo?.benefitImplementations?.find(
            (imp) =>
              imp.benefitImplementationTag ===
              enrollment.benefitImplementationTag,
          );

          const rate = implementation?.currencyCode
            ? currencyRatesPerCountries[implementation.currencyCode]?.rate
            : undefined;

          if (!rate) return enrollment;

          const paidByCompany = enrollment.paidByCompany || 0;
          const paidByEmployee = enrollment.paidByEmployee || 0;

          return {
            ...enrollment,
            paidByCompany:
              (paidByCompany / rate) * (calculateInEur ? 1 : selectedRate),
            paidByEmployee:
              (paidByEmployee / rate) * (calculateInEur ? 1 : selectedRate),
          };
        }) || []
      );
    },
    [currencyRatesPerCountries, selectedCurrency],
  );



  useEffect(() => {
    if(filterCurrency?.value==='all' && defaultCurrencyByUserCountry){
      setSelectedCurrency(defaultCurrencyByUserCountry)
    } else {
      setSelectedCurrency(null);
    }
  }, [filterCurrency, defaultCurrencyByUserCountry]);

    useEffect(() => {
    if (!selectedCurrency) {
      setCompanyInfo(defaultCompanyInfo);
    }else {
      setCompanyInfo({
        ...defaultCompanyInfo,
        employeeEnrollments: convertEnrollmentRates(defaultCompanyInfo),
      });
    }
  }, [selectedCurrency]);

  const filteredCurrencyOptions = currencyOptions.filter(
    (option) => option.value !== filterCurrency?.value,
  );
  return (
    <Flex justify="space-between" wrap="nowrap" gap={"15px"} $align="flex-end">
      <FilteringCombobox
        disabled={currencyRatesLoading}
        error=""
        appearance="outline"
        label={
          selectedCurrency?.text
            ? `${t("hbh.convertTo.label")} ${selectedCurrency.text}`
            : `${t("hbh.convertTo.label")} ${t(
                "hbh.currency.label",
              ).toLowerCase()}`
        }
        setFieldValue={(_, value) => {
          setConvertCurrencyDisabled(false);
          setSelectedCurrency(value);
        }}
        value={selectedCurrency as any}
        noErrorSection
        options={filteredCurrencyOptions}
        name={"currencyCode"}
      />
      {/* <Button
        appearance="primary"
        disabled={
          convertCurrencyDisable ||
          !selectedCurrency ||
          currencyRatesLoading
        }
        onClick={() => {
          setConvertCurrencyDisabled(true);
          setCompanyInfo({
            ...defaultCompanyInfo,
            employeeEnrollments: convertEnrollmentRates(defaultCompanyInfo),
          });
        }}
      >
        {t("hbh.convert.label")}
      </Button> */}
    </Flex>
  );
}

export default SummaryCurrencyConverter;
